// Modules
import {
  NgModule,
  ErrorHandler,
  APP_INITIALIZER,
  isDevMode,
} from "@angular/core";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import * as Sentry from "@sentry/angular";
import { BrowserModule } from "@angular/platform-browser";
import { FormsModule } from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbModule,
} from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { AppAuthModule } from "./app-auth.module";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { ReactiveFormsModule } from "@angular/forms";
import { provideLuxonDateAdapter, MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { MAT_DATE_LOCALE, MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { LanguageStore } from './shared/stores/language.store';

// Components
import { MenuHeaderComponent } from "./layout/header.component";
import { PageAboutUsComponent } from "./pages/page-about-us/page-about-us.component";
import { FilterPanelComponent } from "./shared/components/filter-panel/filter-panel.component";
import { TagSelectorComponent } from "./shared/components/tag-selector/tag-selector.component";
import { TagInputComponent } from "./shared/components/tag-input/tag-input.component";
import { CompanyCardComponent } from "./shared/components/network-card/company-card.component";

import { PageTermsAndConditionsComponent } from "./pages/page-terms-and-conditions/page-terms-and-conditions.component";
import { PagePrivacyStatementComponent } from "./pages/page-privacy-statement/page-privacy-statement.component";
import { PageLostPasswordComponent } from "./pages/page-lost-password/page-lost-password.component";
import { PageProjectsComponent } from "./pages/page-projects/page-projects.component";
import { PageVideosComponent } from "./pages/page-videos/page-videos.component";
import { PageBlogComponent } from "./pages/page-blog/page-blog.component";
import { PageExplanationPlatformComponent } from "./pages/page-explanation-platform/page-explanation-platform.component";
import { PageIAmAStudentComponent } from "./pages/page-i-am-a-student/page-i-am-a-student.component";
import { PageInternationalsComponent } from "./pages/page-internationals/page-internationals.component";
import { PageIAmACompanyComponent } from "./pages/page-i-am-a-company/page-i-am-a-company.component";
import { PageProjectComponent } from "./pages/page-projects/page-project/page-project.component";
import { PageEventComponent } from "./pages/page-events/page-event/page-event.component";
import { PageBlogPostComponent } from "./pages/page-blog/page-blog-post/page-blog-post.component";
import { PageVideoComponent } from "./pages/page-videos/page-video/page-video.component";
import { ModalVideoComponent } from "./core/modal-video/modal-video.component";
import { ModalImageGalleryComponent } from "./core/modal-image-gallery/modal-image-gallery.component";
import { ModalStudentProfileInterestsComponent } from "./student/student-profile/modal-student-profile-interests/modal-student-profile-interests.component";
import { StudentSettingsComponent } from "./student/student-settings/student-settings.component";
import { ModalStudentProfileWorkexperienceComponent } from "./student/student-profile/modal-student-profile-workexperience/modal-student-profile-workexperience.component";
import { ModalStudentProfileEducationComponent } from "./student/student-profile/modal-student-profile-education/modal-student-profile-education.component";
import { ModalStudentProfileLanguageComponent } from "./student/student-profile/modal-student-profile-language/modal-student-profile-language.component";
import { ModalStudentProfileForeignexperienceComponent } from "./student/student-profile/modal-student-profile-foreignexperience/modal-student-profile-foreignexperience.component";
import { ModalStudentProfileHobbyComponent } from "./student/student-profile/modal-student-profile-hobby/modal-student-profile-hobby.component";
import { ModalStudentProfileResumeComponent } from "./student/student-profile/modal-student-profile-resume/modal-student-profile-resume.component";
import { CompanyProfilePublicComponent } from "./companies/company-profile-public/company-profile-public.component";
import { JobCardComponent } from "./shared/components/network-card/job-card.component";
import { StudentCompanySearchComponent } from "./student/student-company-search/student-company-search.component";
import { JobProfileComponent } from "./jobs/job-profile/job-profile.component";
import { StudentApplicationsComponent } from "./student/student-applications/student-applications.component";
import { StudentJobsComponent } from "./student/student-jobs/student-jobs.component";
import { RegisterComponent } from "./authentication/register/register.component";
import { ModalAlertComponent } from "./core/modal-alert/modal-alert.component";
import { StudentRegistrationComponent } from "./student/student-registration/student-registration.component";
import { TeacherRegistrationComponent } from "./teacher/teacher-registration/teacher-registration.component";
import { CompanyProfileComponent } from "./company/company-profile/company-profile.component";
import { ModalTranslateLanguagesComponent } from "./shared/components/modal-translate-languages/modal-translate-languages.component";
import { ModalImageCropperComponent } from "./core/modal-image-cropper/modal-image-cropper.component";
import { ModalCompanyProfileTradenameComponent } from "./company/company-profile/modal-company-profile-tradename/modal-company-profile-tradename.component";
import { CompanyApplicationsComponent } from "./company/company-applications/company-applications.component";
import { ModalCompanyProfileExperiencedayComponent } from "./company/company-profile/modal-company-profile-experienceday/modal-company-profile-experienceday.component";
import { ModalCompanyProfilePhotoComponent } from "./company/company-profile/modal-company-profile-photo/modal-company-profile-photo.component";
import { ModalCompanyProfileVideoComponent } from "./company/company-profile/modal-company-profile-video/modal-company-profile-video.component";

import { StudentCardComponent } from "./shared/components/network-card/student-card.component";
import { CompanyStudentProfileComponent } from "./company/company-student-profile/company-student-profile.component";
import { ModalConfirmComponent } from "./core/modal-confirm/modal-confirm.component";
import { ModalStudentApplicationMotivationComponent } from "./student/student-applications/modal-student-application-motivation/modal-student-application-motivation.component";
import { AdminComponent } from "./admin/admin.component";
import { AdminSideNavigationComponent } from "./admin/admin-side-navigation/admin-side-navigation.component";
import { AdminDashboardComponent } from "./admin/admin-dashboard/admin-dashboard.component";
import { AdminMenuHeaderComponent } from "./admin/admin-menu-header/admin-menu-header.component";
import { AdminFieldofinterestComponent } from "./admin/admin-fieldofinterest/admin-fieldofinterest.component";
import { AdminStudentprofilesComponent } from "./admin/admin-studentprofiles/admin-studentprofiles.component";
import { AdminEventsComponent } from "./admin/admin-events/admin-events.component";
import { AdminCompaniesComponent } from "./admin/admin-companies/admin-companies.component";
import { AdminBlogpostsComponent } from "./admin/admin-blogposts/admin-blogposts.component";
import { AdminProjectsComponent } from "./admin/admin-projects/admin-projects.component";
import { AdminAccountsComponent } from "./admin/admin-accounts/admin-accounts.component";
import { AdminEducationsComponent } from "./admin/admin-educations/admin-educations.component";
import { AdminMatchtestComponent } from "./admin/admin-matchtest/admin-matchtest.component";
import { AdminEducationLevelsComponent } from "./admin/admin-education-levels/admin-education-levels.component";
import { AdminCompanypagesComponent } from "./admin/admin-companypages/admin-companypages.component";
import { AdminJobsComponent } from "./admin/admin-jobs/admin-jobs.component";
import { ModalAdminAreaofinterestComponent } from "./admin/admin-fieldofinterest/modal-admin-areaofinterest/modal-admin-areaofinterest.component";
import { AdminTagsComponent } from "./admin/admin-tags/admin-tags.component";
import { ModalAdminTagComponent } from "./admin/admin-tags/modal-admin-tag/modal-admin-tag.component";
import { ModalAdminCompanyComponent } from "./admin/admin-companies/modal-admin-company/modal-admin-company.component";
import { ModalAdminAccountComponent } from "./admin/admin-accounts/modal-admin-account/modal-admin-account.component";
import { TableComponent } from "./core/table/table.component";
import { ModalAdminCompanyRegisterComponent } from "./admin/admin-companies/modal-admin-company-register/modal-admin-company-register.component";
import { ModalAdminEducationLevelComponent } from "./admin/admin-education-levels/modal-admin-education-level/modal-admin-education-level.component";
import { ModalStudentProfileAvatarComponent } from "./student/student-profile/modal-student-profile-avatar/modal-student-profile-avatar.component";
import { ErrorForbiddenComponent } from "./errors/error-forbidden/error-forbidden.component";
import { AlertComponent } from "./core/alert/alert.component";
import { AdminSchoolsComponent } from "./admin/admin-schools/admin-schools.component";
import { ModalAdminSchoolComponent } from "./admin/admin-schools/modal-admin-school/modal-admin-school.component";
import { AdminSchoolClassesComponent } from "./admin/admin-schools/admin-school-classes/admin-school-classes.component";
import { ModalAdminSchoolClassComponent } from "./admin/admin-schools/admin-school-classes/modal-admin-school-class/modal-admin-school-class.component";
import { ModalCompanyProfileAvatarComponent } from "./company/company-profile/modal-company-profile-avatar/modal-company-profile-avatar.component";
import { ModalCompanyProfileCoverComponent } from "./company/company-profile/modal-company-profile-cover/modal-company-profile-cover.component";
import { ModalStudentApplyComponent } from "./student/modal-student-apply/modal-student-apply.component";
import { ModalAdminStudentprofileComponent } from "./admin/admin-studentprofiles/modal-admin-studentprofile/modal-admin-studentprofile.component";
import { ModalCompanyProfileAccountComponent } from "./company/company-profile/modal-company-profile-account/modal-company-profile-account.component";
import { LoaderComponent } from "./core/loader/loader.component";
import {
  EuropeanDateParserFormatter,
  EuropeanAdapter,
} from "./datepicker-adapter";
import { CompanyAccountComponent } from "./company/company-account/company-account.component";
import { ModalAccountAvatarComponent } from "./account/modal-account-avatar/modal-account-avatar.component";
import { SelectorAreaofinterestsComponent } from "./core/selector-areaofinterests/selector-areaofinterests.component";
import { AdminTeacherprofilesComponent } from "./admin/admin-teacherprofiles/admin-teacherprofiles.component";
import { ModalAdminTeacherprofileComponent } from "./admin/admin-teacherprofiles/modal-admin-teacherprofile/modal-admin-teacherprofile.component";
import { ModalAdminEducationprogramComponent } from "./admin/admin-educations/modal-admin-educationprogram/modal-admin-educationprogram.component";
import { TypeaheadComponent } from "./core/typeahead/typeahead.component";
import { CoreSelectorComponent } from "./core/core-selector/core-selector.component";
import { ModalAdminMatchtestCompetencyComponent } from "./admin/admin-matchtest/modal-admin-matchtest-competency/modal-admin-matchtest-competency.component";
import { ModalAdminMatchtestQuestionComponent } from "./admin/admin-matchtest/modal-admin-matchtest-question/modal-admin-matchtest-question.component";
import { AdminExternalpageComponent } from "./admin/admin-externalpages/admin-externalpage/admin-externalpage.component";
import { AdminExternalpagesComponent } from "./admin/admin-externalpages/admin-externalpages.component";
import { AdminVideosComponent } from "./admin/admin-videos/admin-videos.component";
import { AdminTestimonialsComponent } from "./admin/admin-testimonials/admin-testimonials.component";
import { AdminPagesComponent } from "./admin/admin-pages/admin-pages.component";
import { AccountComponent } from "./account/account.component";
import { PagesComponent } from "./pages/pages.component";
import { ErrorNotfoundComponent } from "./errors/error-notfound/error-notfound.component";
import { ParentalconsentComponent } from "./authentication/parentalconsent/parentalconsent.component";
import { ModalLoaderComponent } from "./core/modal-loader/modal-loader.component";
import { AdminExternalpageSchoolsComponent } from "./admin/admin-externalpage-schools/admin-externalpage-schools.component";
import { ModalCompanyStudentContactComponent } from "./company/company-student-profile/modal-company-student-contact/modal-company-student-contact.component";
import { ExcerptFilter } from "./pipe/pipe.excerpt";
import { DnaCanvasComponent } from "./dna/dna-canvas/dna-canvas.component";
import { ModalCompanyProfileAssignmentComponent } from "./company/company-profile/modal-company-profile-assignment/modal-company-profile-assignment.component";
import { AssignmentProfileComponent } from "./jobs/assignment-profile/assignment-profile.component";
import { TypeaheadEducationprogramComponent } from "./core/typeahead-educationprogram/typeahead-educationprogram.component";

// Global
import "./app-translation-extract";

// Pipes
import { SafeHtmlPipe } from "./pipe/pipe.safehtml";
import { JoinPipe } from "./pipe/pipe-join";
import { ModalCompanyProfileJobmatchesComponent } from "./company/company-profile/modal-company-profile-jobmatches/modal-company-profile-jobmatches.component";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import { ModalAccountEmailComponent } from "./account/modal-account-email/modal-account-email.component";
import { ViewRangeSliderComponent } from "./core/view/view-range-slider/view-range-slider.component";
import { NgxSliderModule } from "@angular-slider/ngx-slider";
import { StudentExperiencedaysComponent } from "./student/student-experiencedays/student-experiencedays.component";
import { AdminMeetdaysComponent } from "./admin/admin-meetdays/admin-meetdays.component";
import { ModalAdminMeetdayComponent } from "./admin/admin-meetdays/modal-admin-meetday/modal-admin-meetday.component";
import { MeetdaySignupComponent } from "./student/meetday-signup/meetday-signup.component";
import { PublicStudentCurrentDnaComponent } from "./student/public/public-student-current-dna/public-student-current-dna.component";
import { AdminExperienceDaysComponent } from "./admin/admin-experience-days/admin-experience-days.component";
import { ModalCompanyElectionSignupComponent } from "./company/company-profile/modal-company-election-signup/modal-company-election-signup.component";
import { AdminToolsComponent } from "./admin/admin-tools/admin-tools.component";
import { AdminCompanyAccountsComponent } from "./admin/admin-companies/admin-company-accounts/admin-company-accounts.component";
import { AdminCompanyElectionSignupModalComponent } from "./admin/admin-companies/admin-company-election-signup-modal/admin-company-election-signup-modal.component";
import { TransferRijrComponent } from "./transfer-rijr/transfer-rijr.component";
import { PageEventsComponent } from "./pages/page-events/page-events.component";
import { PageContactComponent } from "./pages/page-contact/page-contact.component";
import { AdminPostsComponent } from "./admin/admin-posts/admin-posts.component";
import { PagePostsComponent } from "./pages/page-posts/page-posts.component";
import { PagePostComponent } from "./pages/page-posts/page-post/page-post.component";
import { NewsletterModalComponent } from "./newsletters/newsletter-modal/newsletter-modal.component";
import { CompanyApplicationComponent } from "./company/company-applications/company-application/company-application.component";
import { PageSchoolComponent } from "./pages/page-schools/page-school/page-school.component";
import { StudentResumeComponent } from "./student/student-resume/student-resume.component";
import { MarkdownModule } from "ngx-markdown";
import { NgxGpAutocompleteModule } from "@angular-magic/ngx-gp-autocomplete";
import { ImageCropperComponent } from "ngx-image-cropper";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Loader } from "@googlemaps/js-api-loader";
import { StripHtmlPipe } from "./pipe/pipe.striphtml";
import { AuthInterceptor } from "./auth.interceptor";
import { ApiConfiguration } from "./api/api-configuration";
import { environment } from "src/environments/environment";
import { FormlyModule } from "@ngx-formly/core";
import {
  CUSTOM_FIELD_TYPES,
  CUSTOM_WRAPPERS,
} from "./shared/components/formly-fields";
import { Router } from "@angular/router";
import { InputBirthdateComponent } from "./core/form-input-birthdate/input-birthdate.component";
import { StudentMeetdayQrComponent } from "./student/student-meetday-qr/student-meetday-qr.component";
import { CallForOpportunitiesComponent } from "./shared/components/sections/call-for-opportunities/call-for-opportunities.component";
import { SearchContainerComponent } from "./shared/components/search/search-container.component";
import { SearchFiltersComponent } from "./shared/components/search/search-filters.component";
import { SearchResultsGridComponent } from "./shared/components/search/search-results-grid.component";
import { SelectedFiltersPanelComponent } from "./shared/components/search/selected-filters-panel.component";
import { FilterChipComponent } from "./shared/components/search/filter-chip.component";
import { ModalStudentProfileAnnouncementComponent } from "./student/student-profile/modal-student-profile-announcement/modal-student-profile-announcement.component";
import { AdminListPageComponent } from "./admin/admin-list-page/admin-list-page.component";

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function apiConfigFactory(): ApiConfiguration {
  const config = new ApiConfiguration();
  config.rootUrl = environment.apiUrl;
  return config;
}

@NgModule({
  declarations: [
    AppComponent,
    PageAboutUsComponent,
    PageTermsAndConditionsComponent,
    PagePrivacyStatementComponent,
    PageLostPasswordComponent,
    PageProjectsComponent,
    PageVideosComponent,
    PageBlogComponent,
    PageExplanationPlatformComponent,
    PageIAmAStudentComponent,
    PageIAmACompanyComponent,
    PageProjectComponent,
    PageEventComponent,
    PageBlogPostComponent,
    PageVideoComponent,
    ModalVideoComponent,
    ModalImageGalleryComponent,
    StudentResumeComponent,
    ModalStudentProfileInterestsComponent,
    StudentSettingsComponent,
    ModalStudentProfileWorkexperienceComponent,
    ModalStudentProfileEducationComponent,
    ModalStudentProfileLanguageComponent,
    ModalStudentProfileForeignexperienceComponent,
    ModalStudentProfileHobbyComponent,
    ModalStudentProfileResumeComponent,
    CompanyProfilePublicComponent,
    JobProfileComponent,
    StudentApplicationsComponent,
    RegisterComponent,
    ModalAlertComponent,
    StudentRegistrationComponent,
    TeacherRegistrationComponent,
    ModalImageCropperComponent,
    ModalCompanyProfileTradenameComponent,
    CompanyApplicationsComponent,
    ModalCompanyProfileExperiencedayComponent,
    ModalCompanyProfilePhotoComponent,
    ModalCompanyProfileVideoComponent,
    CompanyStudentProfileComponent,
    ModalConfirmComponent,
    ModalStudentApplicationMotivationComponent,
    AdminComponent,
    AdminSideNavigationComponent,
    AdminDashboardComponent,
    AdminMenuHeaderComponent,
    AdminFieldofinterestComponent,
    AdminStudentprofilesComponent,
    AdminEventsComponent,
    AdminCompaniesComponent,
    AdminBlogpostsComponent,
    AdminProjectsComponent,
    AdminAccountsComponent,
    AdminEducationsComponent,
    AdminMatchtestComponent,
    AdminEducationLevelsComponent,
    AdminCompanypagesComponent,
    AdminJobsComponent,
    ModalAdminAreaofinterestComponent,
    AdminTagsComponent,
    ModalAdminTagComponent,
    ModalAdminAccountComponent,
    TableComponent,
    ModalAdminCompanyRegisterComponent,
    ModalAdminEducationLevelComponent,
    ErrorForbiddenComponent,
    AlertComponent,
    AdminSchoolsComponent,
    ModalAdminSchoolComponent,
    AdminSchoolClassesComponent,
    ModalAdminSchoolClassComponent,
    ModalCompanyProfileAvatarComponent,
    ModalCompanyProfileCoverComponent,
    ModalStudentApplyComponent,
    ModalCompanyProfileAccountComponent,
    CompanyAccountComponent,
    ModalAccountAvatarComponent,
    AdminTeacherprofilesComponent,
    ModalAdminTeacherprofileComponent,
    ModalAdminEducationprogramComponent,
    ModalAdminMatchtestQuestionComponent,
    AdminExternalpageComponent,
    AdminExternalpagesComponent,
    AdminVideosComponent,
    AdminTestimonialsComponent,
    AdminPagesComponent,
    ExcerptFilter,
    AccountComponent,
    PagesComponent,
    ErrorNotfoundComponent,
    ParentalconsentComponent,
    ModalLoaderComponent,
    AdminExternalpageSchoolsComponent,
    ModalCompanyStudentContactComponent,
    ModalCompanyProfileAssignmentComponent,
    AssignmentProfileComponent,
    TypeaheadEducationprogramComponent,
    ModalCompanyProfileJobmatchesComponent,
    ModalAccountEmailComponent,
    StudentExperiencedaysComponent,
    AdminMeetdaysComponent,
    PublicStudentCurrentDnaComponent,
    AdminExperienceDaysComponent,
    ModalCompanyElectionSignupComponent,
    AdminToolsComponent,
    AdminCompanyAccountsComponent,
    AdminCompanyElectionSignupModalComponent,
    TransferRijrComponent,
    PageEventsComponent,
    AdminPostsComponent,
    PagePostsComponent,
    PagePostComponent,
    NewsletterModalComponent,
    CompanyApplicationComponent,
    PageSchoolComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    AppAuthModule.forRoot(),
    BrowserModule,
    DnaCanvasComponent,
    JoinPipe,
    MeetdaySignupComponent,
    CompanyCardComponent,
    JobCardComponent,
    StudentCompanySearchComponent,
    StudentJobsComponent,
    StudentMeetdayQrComponent,
    CallForOpportunitiesComponent,
    SearchContainerComponent,
    SearchFiltersComponent,
    SearchResultsGridComponent,
    SelectedFiltersPanelComponent,
    FilterChipComponent,
    ModalStudentProfileAnnouncementComponent,
    AdminListPageComponent,
    ModalAdminMeetdayComponent,
    TypeaheadComponent,
    MenuHeaderComponent,
    NgbModule,
    MatMenuModule,
    MatTableModule,
    MatPaginatorModule,
    FilterPanelComponent,
    TagSelectorComponent,
    TagInputComponent,
    ViewRangeSliderComponent,
    InputBirthdateComponent,
    StripHtmlPipe,
    SafeHtmlPipe,
    PageContactComponent,
    PageInternationalsComponent,
    ModalAdminStudentprofileComponent,
    LoaderComponent,
    CompanyProfileComponent,
    FormsModule,
    SelectorAreaofinterestsComponent,
    CoreSelectorComponent,
    ModalAdminMatchtestCompetencyComponent,
    ModalAdminCompanyComponent,
    ImageCropperComponent,
    ModalStudentProfileAvatarComponent,
    StudentCardComponent,
    MarkdownModule.forRoot(),
    FontAwesomeModule,
    ReactiveFormsModule,
    NgxGpAutocompleteModule,
    NgxSliderModule,
    TranslateModule.forRoot({
      defaultLanguage: "en",
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    CKEditorModule,
    ModalTranslateLanguagesComponent,
    BrowserAnimationsModule,
    FormlyModule.forRoot({
      types: CUSTOM_FIELD_TYPES,
      wrappers: CUSTOM_WRAPPERS,
    }),
  ],
  providers: [
    // Sentry Error Handler (only outside dev mode)
    ...(!isDevMode() && environment.sentry?.enabled
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: false,
            }),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: "AIzaSyCqJqENJ6msGY5O0iPAgF6sSFK-2ZbC1yE",
        libraries: ["places"],
      }),
    },
    { provide: NgbDateAdapter, useClass: EuropeanAdapter },
    { provide: NgbDateParserFormatter, useClass: EuropeanDateParserFormatter },
    {
      provide: "externalUrlRedirectResolver",
      useValue: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        window.location.href = (route.data as any).externalUrl;
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: ApiConfiguration,
      useFactory: apiConfigFactory,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideLuxonDateAdapter(),
    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useValue: { useUtc: false },
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (langStore: LanguageStore) => langStore.currentLanguage().code,
      deps: [LanguageStore],
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: { disabled: true } satisfies RippleGlobalOptions,
    },
  ],
})
export class AppModule {}
